import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, Gift, Share2, CheckCircle, Clock, BarChart2, Zap, MessageCircle, Mail, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';
import { trackEvent } from '../../hooks/useGoogleAnalytics';

const ReferralAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');

  useEffect(() => {
    trackEvent('referral_program_view', { page: 'referral_analytics' });
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const code = `MBT${user?.id?.slice(0, 8)?.toUpperCase()}`;
      const link = `${window.location?.origin}/sign-in?ref=${code}`;
      setReferralCode(code);
      setReferralLink(link);

      const { data } = await supabase
        ?.from('referrals')
        ?.select('*')
        ?.eq('referrer_id', user?.id)
        ?.order('invited_at', { ascending: false });

      setReferrals(data || []);
    } catch (err) {
      console.error('Failed to load referral analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (channel) => {
    trackEvent('referral_share_click', { channel });
    let url = '';
    const msg = `Track your health daily with MyBodyToday. Use my link: ${referralLink}`;
    switch (channel) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent('Track your health with MyBodyToday')}&body=${encodeURIComponent(msg)}`;
        break;
      case 'link':
        navigator.clipboard?.writeText(referralLink);
        return;
      default:
        return;
    }
    window.open(url, '_blank');
  };

  // Computed stats
  const totalInvites = referrals?.length;
  const converted = referrals?.filter(r => r?.status === 'converted');
  const pending = referrals?.filter(r => r?.status !== 'converted');
  const conversionRate = totalInvites > 0 ? Math.round((converted?.length / totalInvites) * 100) : 0;
  const activeReferrals = converted?.length;

  // Milestone progress
  const milestone1Target = 5;
  const milestone2Target = 10;
  const milestone1Progress = Math.min(activeReferrals, milestone1Target);
  const milestone2Progress = Math.min(activeReferrals, milestone2Target);
  const milestone1Reached = activeReferrals >= milestone1Target;
  const milestone2Reached = activeReferrals >= milestone2Target;

  const neededForM1 = Math.max(0, milestone1Target - activeReferrals);
  const neededForM2 = Math.max(0, milestone2Target - activeReferrals);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getMilestoneContribution = (referral) => {
    const idx = converted?.findIndex(r => r?.id === referral?.id);
    if (idx === -1) return '—';
    if (idx < 5) return 'Doctor Summary';
    if (idx < 10) return 'Full Premium';
    return 'Bonus';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading referral analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <UserProfileHeader />
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/referral-program')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <BarChart2 className="w-6 h-6 text-indigo-600" />
              <h1 className="text-xl font-bold text-gray-900">Referral Analytics</h1>
            </div>
            <div className="flex items-center gap-3">
              <CloseButton onClose={() => navigate('/referral-program')} navigateTo="/referral-program" ariaLabel="Close analytics" />
              <LogoutButton />
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Invites Sent', value: totalInvites, icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50' },
            { label: 'Successful Sign-ups', value: activeReferrals, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Conversion Rate', value: `${conversionRate}%`, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Active Referrals', value: activeReferrals, icon: Zap, color: 'text-purple-600', bg: 'bg-purple-50' },
          ]?.map((stat, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <div className={`w-10 h-10 ${stat?.bg} rounded-lg flex items-center justify-center mb-3`}>
                <stat.icon className={`w-5 h-5 ${stat?.color}`} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{stat?.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Milestone Progress */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
            <Gift className="w-5 h-5 text-indigo-600" />
            Milestone Progress
          </h2>
          <div className="space-y-5">
            {/* Milestone 1 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-sm font-semibold text-gray-800">Doctor Summary</span>
                  {milestone1Reached && <span className="ml-2 text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">Unlocked ✓</span>}
                </div>
                <span className="text-sm font-bold text-gray-700">{milestone1Progress} / {milestone1Target}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${milestone1Reached ? 'bg-green-500' : 'bg-indigo-500'}`}
                  style={{ width: `${(milestone1Progress / milestone1Target) * 100}%` }}
                />
              </div>
              {!milestone1Reached && (
                <p className="text-xs text-gray-500 mt-1.5">
                  Refer <span className="font-bold text-indigo-600">{neededForM1} more</span> to unlock Doctor Summary
                </p>
              )}
            </div>

            {/* Milestone 2 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-sm font-semibold text-gray-800">Full Premium</span>
                  {milestone2Reached && <span className="ml-2 text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">Unlocked ✓</span>}
                </div>
                <span className="text-sm font-bold text-gray-700">{milestone2Progress} / {milestone2Target}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all ${milestone2Reached ? 'bg-green-500' : 'bg-purple-500'}`}
                  style={{ width: `${(milestone2Progress / milestone2Target) * 100}%` }}
                />
              </div>
              {!milestone2Reached && (
                <p className="text-xs text-gray-500 mt-1.5">
                  Refer <span className="font-bold text-purple-600">{neededForM2} more</span> to unlock Full Premium <span className="text-gray-400">(worth £4.99/month)</span>
                </p>
              )}
            </div>
          </div>

          {/* Current reward status */}
          <div className="mt-5 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
            <p className="text-sm font-semibold text-indigo-800">
              {milestone2Reached
                ? '🎉 You\'ve unlocked Full Premium for free!'
                : milestone1Reached
                  ? '✅ Doctor Summary unlocked! Refer 5 more to unlock Full Premium.'
                  : `🎯 Refer ${neededForM1} more friend${neededForM1 !== 1 ? 's' : ''} to unlock Doctor Summary for free.`}
            </p>
          </div>
        </div>

        {/* Earning Projections */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Earning Projections
          </h2>
          <div className="space-y-3">
            {!milestone1Reached && (
              <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-lg">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-indigo-700">{neededForM1}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-indigo-900">Refer {neededForM1} more to unlock Doctor Summary</p>
                  <p className="text-xs text-indigo-600 mt-0.5">Generate professional consultation reports for your doctor</p>
                </div>
              </div>
            )}
            {!milestone2Reached && (
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-purple-700">{neededForM2}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-purple-900">Refer {neededForM2} more to unlock Full Premium</p>
                  <p className="text-xs text-purple-600 mt-0.5">Worth £4.99/month — includes all premium features forever</p>
                </div>
              </div>
            )}
            {milestone2Reached && (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm font-semibold text-green-800">All milestones reached! You're on Full Premium for free.</p>
              </div>
            )}
          </div>
        </div>

        {/* Invite Performance */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-green-600" />
            Invite Performance
          </h2>
          <div className="grid grid-cols-3 gap-4 mb-5">
            {[
              { label: 'WhatsApp', icon: MessageCircle, color: 'text-green-600', bg: 'bg-green-50', channel: 'whatsapp' },
              { label: 'Email', icon: Mail, color: 'text-blue-600', bg: 'bg-blue-50', channel: 'email' },
              { label: 'Link', icon: LinkIcon, color: 'text-gray-600', bg: 'bg-gray-50', channel: 'link' },
            ]?.map((ch) => (
              <button
                key={ch?.channel}
                onClick={() => handleShare(ch?.channel)}
                className={`flex flex-col items-center gap-2 p-4 ${ch?.bg} rounded-xl border border-gray-200 hover:shadow-md transition-all`}
              >
                <ch.icon className={`w-6 h-6 ${ch?.color}`} />
                <span className="text-xs font-semibold text-gray-700">{ch?.label}</span>
              </button>
            ))}
          </div>
          <div className="text-xs text-gray-500 text-center">
            Total shares sent: <span className="font-bold text-gray-700">{totalInvites}</span>
          </div>
        </div>

        {/* Referral History Table */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Referral History
            </h2>
          </div>
          {referrals?.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No referrals yet. Share your link to get started!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date Invited</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Milestone</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {referrals?.map((referral, i) => (
                    <tr key={referral?.id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 font-medium truncate max-w-[160px]">
                        {referral?.referee_email || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(referral?.invited_at)}</td>
                      <td className="px-4 py-3">
                        {referral?.status === 'converted' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                            <CheckCircle className="w-3 h-3" /> Signed Up
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                            <Clock className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {referral?.status === 'converted' ? getMilestoneContribution(referral) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate('/referral-program')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 text-white rounded-xl font-bold text-base hover:bg-indigo-700 transition-colors shadow-lg"
          >
            <Share2 className="w-5 h-5" />
            Invite More Friends
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferralAnalytics;
